import GolfIcon from '@/components/GolfIcon';
import { FriendRequestAcceptedNotification } from '@/components/notifications/FriendRequestAcceptedNotification';
import { FriendRequestNotification } from '@/components/notifications/FriendRequestNotification';
import { GameInvitationNotification } from '@/components/notifications/GameInvitationNotification';
import { NewPostNotification } from '@/components/notifications/NewPostNotification';
import { PostCommentNotification } from '@/components/notifications/PostCommentNotification';
import { PostLikeNotification } from '@/components/notifications/PostLikeNotification';
import { useUserProfile } from '@/context/UserProfileContext';
import { supabase } from '@/utils/supabase';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonSpinner
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { RealtimeChannel } from '@supabase/supabase-js';

type Notification = {
  id: number;
  userId: string;
  type: string;
  data: any;
  read: boolean;
  createdAt: string;
  updatedAt: string;
  senderInfo?: {
    name: string;
    username: string;
    avatar: string;
  }[];
};

const NotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { profile } = useUserProfile();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const userId = profile?.userId;

  document.title = t('notifications');

  useEffect(() => {
    let notificationsChannel: RealtimeChannel;

    const fetchNotifications = async () => {
      if (!userId) return;

      setLoading(true);
      try {
        // Fetch notifications from Supabase
        const { data: notificationsData, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (error) {
          throw error;
        }

        if (notificationsData) {
          // Format notifications to match our component expectations
          const formattedNotifications = await Promise.all(
            notificationsData.map(async (notification) => {
              // Convert snake_case to camelCase
              const formattedNotification: Notification = {
                id: notification.id,
                userId: notification.user_id,
                type: notification.type,
                data: notification.data,
                read: notification.read,
                createdAt: notification.created_at,
                updatedAt: notification.updated_at
              };

              // Fetch sender info if needed
              if (notification.data.senderId || notification.data.senderIds) {
                const senderIds = notification.data.senderIds || [notification.data.senderId];
                const senderInfoPromises = senderIds.map(async (senderId: string) => {
                  const { data: senderData } = await supabase
                    .from('profiles')
                    .select('first_name, last_name, username, imageUrl')
                    .eq('userId', senderId)
                    .single();

                  return senderData ? {
                    name: senderData.first_name + ' ' + senderData.last_name,
                    username: senderData.username,
                    avatar: senderData.imageUrl
                  } : null;
                });

                const senderInfo = (await Promise.all(senderInfoPromises)).filter(Boolean);
                formattedNotification.senderInfo = senderInfo;
              }

              return formattedNotification;
            })
          );

          setNotifications(formattedNotifications);

          // Mark all as read when viewing the notifications page
          markAllAsRead();
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    const setupRealtimeSubscription = () => {
      if (!userId) return;

      // Subscribe to realtime notifications
      notificationsChannel = supabase
        .channel(`notifications:${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            // Handle different events
            if (payload.eventType === 'INSERT') {
              fetchNotifications(); // Refresh all notifications
            } else if (payload.eventType === 'UPDATE') {
              // Update the specific notification
              setNotifications(prev =>
                prev.map(n => n.id === payload.new.id ? {
                  ...n,
                  read: payload.new.read,
                  updatedAt: payload.new.updated_at
                } : n)
              );
            } else if (payload.eventType === 'DELETE') {
              // Remove the deleted notification
              setNotifications(prev =>
                prev.filter(n => n.id !== payload.old.id)
              );
            }
          }
        )
        .subscribe();
    };

    fetchNotifications();
    setupRealtimeSubscription();

    return () => {
      // Cleanup subscription
      if (notificationsChannel) {
        supabase.removeChannel(notificationsChannel);
      }
    };
  }, [userId]);

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) {
        throw error;
      }

      // Update local state
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true }))
      );
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const openSettings = () => {
    history.push('/settings/notifications');
  };

  const handleDeleteNotification = async (notificationId: number) => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton text="" defaultHref="/dashboard" />
          </IonButtons>
          <IonTitle>{t('notifications')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={openSettings} fill="clear">
              <GolfIcon name="settings" className="size-5" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {loading ? (
          <div className="flex h-full flex-col items-center justify-center">
            <IonSpinner name="circular" />
            <p className="mt-2 text-muted-foreground">{t('loading')}</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center">
            <GolfIcon name="notification" className="mb-4 size-16" />
            <p className="text-muted-foreground">{t('no_notifications')}</p>
          </div>
        ) : (
          <ul className="md:border-x mx-auto max-w-xl">
            {notifications.map((notification) => {
              switch (notification.type) {
                case 'friend_request':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex items-center space-x-4">
                        <FriendRequestNotification
                          userId={notification.data.senderId!}
                          friendshipId={notification.data.friendshipId}
                          senderInfo={notification.senderInfo?.[0]!}
                        />
                      </div>

                      <IonButton
                        fill="clear"
                        className='hidden'
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        {t('delete')}
                      </IonButton>
                    </li>
                  );
                case 'friend_request_accepted':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex items-center space-x-4">
                        <FriendRequestAcceptedNotification
                          userId={notification.data.senderId!}
                          senderInfo={notification.senderInfo?.[0]!}
                        />
                      </div>
                      <IonButton
                        fill="clear"
                        className='hidden'
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        {t('delete')}
                      </IonButton>
                    </li>
                  );
                case 'new_post':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex w-full items-center space-x-4">
                        <NewPostNotification
                          postData={notification.data}
                          senderInfo={notification.senderInfo?.[0]!}
                        />
                      </div>
                      <IonButton
                        fill="clear"
                        className='hidden'
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        {t('delete')}
                      </IonButton>
                    </li>
                  );
                case 'post_like':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex w-full items-center space-x-4">
                        <PostLikeNotification
                          senderInfo={notification.senderInfo!}
                          likesData={notification.data}
                        />
                      </div>
                      <IonButton
                        fill="clear"
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        {t('delete')}
                      </IonButton>
                    </li>
                  );
                case 'post_comment':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex w-full items-center space-x-4">
                        <PostCommentNotification
                          senderInfo={notification.senderInfo!}
                          commentData={notification.data}
                        />
                      </div>
                      <IonButton
                        fill="clear"
                        className='hidden'
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        {t('delete')}
                      </IonButton>
                    </li>
                  );
                case 'game_invitation':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex w-full items-center">
                        <GameInvitationNotification
                          gameId={notification.data.gameId.toString()}
                          gameName={notification.data.gameName}
                          gameDate={notification.data.gameDate}
                          clubName={notification.data.clubName}
                          courseName={notification.data.courseName}
                          senderInfo={notification.senderInfo?.[0]!}
                          notificationId={notification.id.toString()}
                          userId={userId!}
                        />
                      </div>
                    </li>
                  );
                default:
                  return null;
              }
            })}
          </ul>
        )}
      </IonContent>
    </IonPage>
  );
};

export default NotificationsPage;
